import PointCloudBackgrounds from "layout/PointCloudBackgrounds";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {useImages} from "../../contexts/images";
import {useSetting} from "../../contexts/setting";
import cn from "classnames";
import "./index.css";
import {DEFAULT_BACKGROUND_DATA} from "../../data/backgrounds";
import {
  getLivestreamDefaultTransform,
  getLivestreamSeedBackgrounds,
  getLivestreamSelectorBackground,
  LIVESTREAM_TEMPLATE_IDS,
} from "../../pages/Livestream/productBackgrounds";
import {palettes} from "../../data/palettes";
import {
  checkIfAnimated,
  fileToDataUrl,
  getSizeFromBase64,
  getThumbnailUrl,
  isSvgFile,
} from "../../libs/bg-utils";
import {buildBgCatalogIndex, lookupBgImage} from "../../libs/bgCatalogIndex";
import {activePinnedBgUrl} from "../../libs/sceneBgSource";
import {deriveBgUrl} from "../../hooks/useBgUrl";
import {coverFitTransform} from "../../libs/utils";
import {pageGalleryTiles} from "./galleryTiles.ts";
import {useChannelSettings} from "../../hooks/useChannelSettings";
import {
  firstVisibleVariation,
  hiddenBackgroundsOf,
  movedVariation,
  orderedBySet,
} from "../../libs/selectorVisibility";
import {useSetBackgroundOrder} from "../../hooks/useSetBackgroundOrder";
import {useBackgroundNumbers, useWriteUrlBackground} from "../../hooks/useBackgroundNumbers";
// For a background that has no tile number: no numbers needed.
import {hasPageTile, writeUrlBackground as writeBackgroundParam} from "../../libs/bgNumbering";
import {
  bannerImagesFollowBackground,
  customBgsAfterBgSelect,
  shouldClearSharedUploadOnBgSelect,
} from "./sharedUpload.ts";
import {isLinkGalleryTileCurrent, linkTileOf, showsLinkBackground} from "./linkTile.ts";
import {knownTemplates, knownTemplatesFor} from "../../data/templates.js";
import {useTemplateSelection} from "../../hooks/useTemplateSelection";
import {offersGenerativeTile} from "../../data/products/index.js";
import {generativePreview} from "./generativePreview.ts";
import {useSceneScope} from "../../hooks/useSceneScope";
import {RELEASES_SETS, UPLOAD_MAX_BYTES} from "../../data/constants";
import {track} from "libs/analytics";
import {API} from "../../api.js";
import {openErrorDialog} from "../../layout/ErrorDialog";
import {useBgDerivation} from "../../hooks/useBgDerivation";
import {useBgCatalogIndex} from "../../hooks/useBgCatalogIndex";
import {useBgListEditing} from "../../hooks/useBgList";
import {useBgListEditor} from "../../hooks/useBgListEditor";
import {hasVariation} from "../../libs/bgListEditor";
import {draftUploadVariation, isDraftUploadVariation} from "../../libs/bgIdentity";
import {
  addSessionBgUpload,
  sessionBgScope,
  useSessionBgUploads,
  withSessionBgUploads,
} from "../../libs/sessionBgUploads";
import {useAuth} from "../../hooks/useAuth";
import {useFeatures} from "../../hooks/useFeature";
import {usePromoteUpload} from "../../hooks/usePromoteUpload";
import {can} from "data/permissions";
import BgListTiles from "./BgListTiles";
import ColorPicker from "../ColorPicker";
import {parseColorInput} from "libs/colorPicker";
import {BaseColorInput} from "./BaseColorInput";
import {BgTilePreview} from "./BgTilePreview";
import {
  layoutPreviewCandidates,
  pickTilePreviewTemplate,
  previewCandidates,
  tileHeightFor,
  tilePreviewLayout,
} from "./tilePreviewGeometry";
import {useBgTransformContext} from "../../contexts/bgTransform";
import {useApplyBackgroundAppearance} from "../../hooks/useSceneBackgroundSelection";
import {useBgImageSizes} from "../../hooks/useBgImageSizes";
import {useCurrentSet} from "../../hooks/useCurrentSet";
import {getRouteTemplates} from "utils/routeTemplates";
import BgListControls, {BgEditToggle, BgRemovedNotice, SetOrderControls} from "./BgListControls";

// Reference-stable fallback — a fresh `[]` default would re-fire downstream deps.
const EMPTY_BG_URLS = [];

/**
 * Download the full-size source image to disk. Handles both HTTP(S) URLs
 * (fetched as blob) and data URLs (base64 decoded to blob). The synthetic
 * <a download> click works for both — direct `download` attribute on <a>
 * is ignored for cross-origin URLs, so we always go through blob + objectURL.
 */
async function downloadBackgroundImage(url, filename) {
  try {
    let blob;
    if (url.startsWith("data:")) {
      // data URL: extract base64 payload and convert to blob
      const commaIdx = url.indexOf(",");
      if (commaIdx === -1) throw new Error("Invalid data URL");
      const header = url.slice(0, commaIdx);
      const base64 = url.slice(commaIdx + 1);
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";
      const binary = atob(base64);
      const len = binary.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
      blob = new Blob([arr], {type: mimeType});
    } else {
      // HTTP(S) URL: fetch as blob
      const r = await fetch(url);
      if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
      blob = await r.blob();
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error("[BgSelector] background download failed:", err);
  }
}

const Button = ({
  value,
  handleClick,
  src,
  fullSrc,
  isCurrent,
  isLoading,
  onRemove,
  onAdd,
  preview,
  className,
  title,
}) => {
  const btnClass = cn("bg-button", className, {
    selected: isCurrent,
    loading: isLoading,
    "bg-button--preview": preview,
  });

  const handleRemove = (e) => {
    // Don't bubble to the parent <button> click (which selects the bg).
    e.stopPropagation();
    onRemove();
  };

  const handleAdd = (e) => {
    // Don't bubble to the parent <button> click (which selects the bg).
    e.stopPropagation();
    onAdd();
  };

  const handleDownload = (e) => {
    // Don't bubble to the parent <button> click (which selects the bg).
    e.stopPropagation();
    if (!fullSrc) return;
    // Strip any hash fragment we appended to satisfy findBgUrl's pattern
    // (e.g. `…<id>.png#/JB.png` for synthesized Ads sources). The CDN
    // ignores it anyway, but we want a clean filename.
    const cleanUrl = fullSrc.split("#")[0];
    // Derive the filename from the path only — drop any `?v=<etag>` cache-bust
    // query so the regex still matches, but keep it on the fetched URL.
    const filenameMatch = cleanUrl.split("?")[0].match(/[^/]+\.[a-z0-9]+$/i);
    const extension = cleanUrl.startsWith("data:image/svg+xml") ? "svg" : "png";
    const filename = filenameMatch ? `bg-${value}-${filenameMatch[0]}` : `bg-${value}.${extension}`;
    downloadBackgroundImage(cleanUrl, filename);
  };

  return (
    <button
      value={value}
      className={btnClass}
      title={title}
      onClick={() => handleClick(value)}
      style={
        preview
          ? {width: preview.layout.tileWidth, height: preview.layout.tileHeight}
          : {backgroundImage: src ? `url(${src})` : "none"}
      }
    >
      {preview && <BgTilePreview src={preview.thumbnailUrl ?? src} {...preview} />}
      {isLoading && <div className="bg-loading-indicator">Loading...</div>}
      {onAdd && (
        <span
          className="bg-button__add"
          role="button"
          tabIndex={0}
          aria-label="Add to backgrounds"
          title="Add to backgrounds: the first one for everyone"
          onClick={handleAdd}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleAdd(e);
            }
          }}
        >
          +
        </span>
      )}
      {onRemove && (
        <span
          className="bg-button__remove"
          role="button"
          tabIndex={0}
          aria-label="Remove background"
          title="Remove background"
          onClick={handleRemove}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleRemove(e);
            }
          }}
        >
          ×
        </span>
      )}
      {fullSrc && (
        <span
          className="bg-button__download"
          role="button"
          tabIndex={0}
          aria-label="Download full image"
          title="Download full image"
          onClick={handleDownload}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleDownload(e);
            }
          }}
        >
          {/* Floppy-disk save icon (Material Design — public domain). */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-7 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm5-10H5V5h12v4z" />
          </svg>
        </span>
      )}
    </button>
  );
};

/**
 * Background image opacity, in percent. The base colour shows through the
 * faded image, so the two controls sit in the same panel. Some designs (the
 * JetBrains Air release artwork) are authored semi-transparent, and their
 * default scene sets this below 100.
 */
const OpacitySlider = ({id, value, onChange}) => (
  <div className="slider-control">
    <label htmlFor={id}>Opacity</label>
    <input
      id={id}
      type="range"
      min="0"
      max="100"
      value={value ?? 100}
      onChange={(e) => onChange(Number(e.target.value))}
      className="slider"
    />
    <span className="slider-value">{value ?? 100}%</span>
  </div>
);

const UploadButton = ({handleClick, handleChange, isUploading}) => {
  const btnClass = cn("bg-button upload-button", {"upload-button--uploading": isUploading});
  return (
    <div>
      <input
        type="file"
        accept="image/*,video/*"
        onChange={(e) => handleChange(e)}
        onClick={(e) => handleClick(e)}
        style={{display: "none"}}
        id="upload-button"
      />
      <label htmlFor="upload-button" className={btnClass} aria-busy={isUploading}>
        {/* While a picked file uploads, a spinner replaces the icon and the text. */}
        {isUploading ? (
          <span className="upload-button__spinner" role="status" aria-label="Uploading" />
        ) : (
          <span>Upload File</span>
        )}
      </label>
    </div>
  );
};

const GenerativeButton = ({handleClick, isCurrent}) => {
  const {product} = useSetting();
  // A static gradient preview from the palette colors.
  const {backgroundImage, baseColor} = generativePreview(palettes[product.label]);
  const btnClass = cn("bg-button", "generative-button", {selected: isCurrent});
  return (
    <button
      className={btnClass}
      onClick={handleClick}
      title="Generative gradient"
      style={{"--generative-base": baseColor}}
    >
      <div key={product.label} className="generative-preview" style={{backgroundImage}} />
    </button>
  );
};

const BgSelector = ({
  amount = 0,
  isLoading,
  transforms = {},
  allBgUrls = EMPTY_BG_URLS,
  // Catalog variations in display order (null off the catalog path). The
  // tile order comes from here; `allBgUrls` is indexed by variation.
  displayOrder = null,
  // On a saved link: the published gallery of the scene's product, shown in
  // place of the legacy tiles, and the background the link opened with
  // (linkTile.ts). Null elsewhere.
  linkGallery = null,
  linkBackground = null,
  controlled = null,
  // The route's default scene has applied its background. Until then
  // `bgOption` can be the "1" placeholder (contexts/setting.js).
  isRouteDefaultReady = true,
}) => {
  const {
    bgOption: sceneBgOption,
    editBgOption,
    type,
    locked,
    product,
    base,
    bases,
    editBase,
    channel,
    dataChannel,
    dataType,
    visible,
    layoutOption,
    editIsBgToolsExpanded,
    editIsImageToolsExpanded,
    editCurrentImageToolsId,
    isAnimationPaused,
    toggleAnimationPaused,
    generativeSpeed,
    editGenerativeSpeed,
    generativeCount,
    editGenerativeCount,
    generativeStops,
    editGenerativeStops,
    generativeGradients,
    regenerateGradients,
    hiddenTemplates,
    extraTemplates,
    pinnedSceneBg,
    editPinnedSceneBg,
  } = useSetting();
  const bgOption = controlled ? controlled.option : sceneBgOption;
  const {
    editTransform,
    editCustomBg,
    customBg,
    customBgs,
    editBgX,
    editBgY,
    editBgScale,
    editCustomBgs,
    loadCustomBgs,
    editCustomBgUploaded,
    loadCustomBgUploaded,
    bgBrightness,
    editBgBrightness,
    bgBlurValue,
    editBgBlurValue,
    bgOpacity,
    editBgOpacity,
    editIsBgAnimation,
    incPendingUpload,
    decPendingUpload,
    setUserBgAdjustment,
    bgAdjustmentOrder,
    bgPresets,
  } = useImages();
  // Phase-2 (issue #528, 2a). Off: legacy editTransform writes. On: tile clicks
  // are selection-only (the selector derives the crop from its catalog layer)
  // and explicit cover-fit / reset actions write per-template user intent.
  // Scoped to non-saved routes (see App.js): saved scenes keep the legacy path.
  const bgDerivation = useBgDerivation();
  // Display urls + thumbnails, published by App under the flag (#567, B3).
  const catalogIndex = useBgCatalogIndex(dataChannel, dataType);
  // The backgrounds an admin took out of this gallery's tiles (Defaults panel,
  // libs/selectorVisibility.ts): for every set, or on releases for the page's
  // set only. The page has no such background: a URL that names one opens the
  // first tile (see below). A saved scene still shows its own background.
  const {data: channelSettings} = useChannelSettings(dataChannel);
  const currentSet = useCurrentSet();
  const hiddenBgs = useMemo(
    () => hiddenBackgroundsOf(channelSettings, dataType, product?.value, currentSet),
    [channelSettings, dataType, product?.value, currentSet]
  );
  // A saved link shows the gallery its product page shows, not the legacy
  // tiles: those still hold backgrounds an admin deleted (App's linkGallery).
  const linkGalleryIndex = useMemo(
    () => (linkGallery ? buildBgCatalogIndex(linkGallery, product.value) : null),
    [linkGallery, product.value]
  );
  const galleryIndex = controlled ? linkGalleryIndex : (catalogIndex ?? linkGalleryIndex);
  const galleryOrder = useMemo(
    () => displayOrder ?? linkGallery?.map((background) => background.variation) ?? null,
    [displayOrder, linkGallery]
  );
  // The tiles follow the catalog. An empty gallery of a link has no index,
  // and still offers no legacy tile.
  const isCatalogGallery = Boolean(catalogIndex && displayOrder) || linkGallery !== null;
  const {sceneId} = useParams();
  const location = useLocation();
  // Writes the background into the URL by its tile number
  // (hooks/useBackgroundNumbers.ts).
  const writeUrlBackground = useWriteUrlBackground();
  // Editable background list (plan D1). The editor reads the SAME React Query
  // entry App publishes the display list from, so a reorder moves the tile and
  // the rendered banner together, with no second copy of the list.
  const bgListEditing = useBgListEditing(dataChannel) && !controlled;
  // Crop previews: each tile shows its background as one horizontal banner of
  // the page crops it — the one whose crop changed last (see
  // tilePreviewGeometry.ts). Every tile has the same height.
  const {previewsEnabled, getTileImage, getTileTransform, getTileSavedPreviewTemplate, getTileLayoutImages, getTileBase} =
    useBgTransformContext();
  const applyBackgroundAppearance = useApplyBackgroundAppearance();
  const templateSelection = useTemplateSelection();
  const routeTemplates = useMemo(
    () =>
      getRouteTemplates(dataChannel, {
        layoutOption,
        set: currentSet,
        product: product?.value,
        type: dataType,
        hiddenTemplates,
        extraTemplates,
        selection: templateSelection,
      }),
    [
      dataChannel,
      dataType,
      layoutOption,
      currentSet,
      product,
      hiddenTemplates,
      extraTemplates,
      templateSelection,
    ]
  );
  // Scene scopes: the sliders and gallery picks reach every banner or the
  // selection (hooks/useSceneScope). Off a scopes channel the hook falls
  // through to the shared writes below unchanged. With the page's banners the
  // sliders show the look that the banners show.
  const routeTemplateIds = useMemo(() => routeTemplates.map((template) => template.id), [routeTemplates]);
  const scope = useSceneScope({templateIds: routeTemplateIds});
  const previewTemplates = useMemo(
    () => previewCandidates(routeTemplates, visible),
    [routeTemplates, visible]
  );
  const tileHeight = useMemo(() => tileHeightFor(previewTemplates), [previewTemplates]);
  const {role} = useAuth();
  // Role features (data/features.ts). A tile that the scene shows stays, so the
  // panel shows what is current.
  const features = useFeatures();
  const bgListEditor = useBgListEditor({
    channel: dataChannel,
    type: dataType,
    product: product?.value,
    enabled: bgListEditing,
  });
  // The admin's list shows this page's temporary tiles after it, as the
  // gallery does for everybody else (App's displayBackgrounds). With the
  // pencil on they stay after the sortable list: they are no part of the
  // draft, so they do not move and "Publish" leaves them out, but they stay
  // on screen and selected for "Save as default".
  const sessionUploads = useSessionBgUploads(sessionBgScope(dataChannel, dataType, product?.value));
  // A releases set can have its own tile order (hooks/useSetBackgroundOrder.ts).
  // The editor's list stays in the product's order: "Publish" writes that one.
  const setBgOrder = useSetBackgroundOrder();
  const setOrderedList = useMemo(
    () => orderedBySet(bgListEditor.list, (entry) => entry.variation, setBgOrder.order),
    [bgListEditor.list, setBgOrder.order]
  );
  const listTiles = useMemo(
    () => withSessionBgUploads(setOrderedList, sessionUploads),
    [setOrderedList, sessionUploads]
  );
  const sessionTilesAfterList = useMemo(
    () => (listTiles ?? []).slice(bgListEditor.list?.length ?? 0),
    [listTiles, bgListEditor.list]
  );
  const [removedNoticeAt, setRemovedNoticeAt] = useState(0);
  // The pencil. Editing affordances — history, reset, publish, drag, per-tile
  // remove and download — exist only while it is on, and it starts off on
  // every visit, so picking a background never brushes past a delete.
  const [isBgEditMode, setIsBgEditMode] = useState(false);
  const isBgEditorOpen = bgListEditing && features["bg.list-editor"] && isBgEditMode;
  // Which order a drag edits: the product's (every set, a draft until
  // "Publish"), or the page set's own (saved at once for everyone). Until the
  // admin chooses "All sets" on this opening of the editor, it is the set's
  // order, also when the set has none yet: an admin who edits the Splash page
  // must not change every set (Sergey, 2026-10-27: "настраивая всего лишь
  // сет сплеша я нажму и перетру все сеты").
  const [orderScopeChoice, setOrderScopeChoice] = useState(null);
  const isSetOrderScope =
    orderScopeChoice !== null && orderScopeChoice.set === setBgOrder.set
      ? orderScopeChoice.isSetScope
      : true;
  const chooseOrderScope = useCallback(
    (isSetScope) => setOrderScopeChoice({set: setBgOrder.set, isSetScope}),
    [setBgOrder.set]
  );
  const setLabel = RELEASES_SETS.find((set) => set.value === setBgOrder.set)?.label;
  const isEditingSetOrder = isBgEditorOpen && setBgOrder.isAvailable && isSetOrderScope;
  const [, setSearchParams] = useSearchParams();
  const [loadingStates, setLoadingStates] = useState({});
  const [currentBgUrls, setCurrentBgUrls] = useState([]);
  const processedRef = useRef(false);
  // Last committed (non-blob) customBg, used as the revert target when an
  // upload fails. Reading `customBg` directly from the closure would, under
  // concurrent picks, capture an intermediate `blob:` URL — which is then
  // revoked when the *other* upload completes, leaving us reverting to a
  // dead URL. Updated only when customBg moves to a non-blob value.
  const lastCommittedBgRef = useRef(
    typeof customBg === "string" && !customBg.startsWith("blob:") ? customBg : ""
  );
  useEffect(() => {
    if (typeof customBg === "string" && !customBg.startsWith("blob:")) {
      lastCommittedBgRef.current = customBg;
    }
  }, [customBg]);
  // Ads renders the shared upload from customBgs.main instead of customBg.
  // Keep the same stable rollback target for that storage slot.
  const lastCommittedAdsBgRef = useRef(
    typeof customBgs.main === "string" && !customBgs.main.startsWith("blob:")
      ? customBgs.main
      : undefined
  );
  useEffect(() => {
    if (typeof customBgs.main === "string" && !customBgs.main.startsWith("blob:")) {
      lastCommittedAdsBgRef.current = customBgs.main;
    }
  }, [customBgs.main]);

  const getUrlAtIndex = useCallback(
    (index) => {
      const item = currentBgUrls.find((item) => item && item.index === index);
      return item && item.url ? item.url : "";
    },
    [currentBgUrls]
  );

  const isAllProducts = product.value === "ALL";
  const isGoogleDisplayNetwork = channel.value === "ads" && type.value === "google-display-network";

  // Resolve the image and banner together. A preset can have different pixels
  // and dimensions from the catalog image, even for the same background.
  const tileSources = useMemo(() => {
    const entries = bgListEditing
      ? listTiles
      : currentBgUrls
          .filter(Boolean)
          .map((item) => ({variation: item.index, imageUrl: item.fullUrl, thumbnailUrl: item.url}));
    const thumbnails = new Map(
      entries
        .filter((entry) => entry.thumbnailUrl && entry.thumbnailUrl !== entry.imageUrl)
        .map((entry) => [entry.imageUrl, entry.thumbnailUrl])
    );
    const sources = new Map();
    for (const entry of entries) {
      const savedId = getTileSavedPreviewTemplate(entry.variation);
      const layoutImages = getTileLayoutImages(entry.variation);
      if (savedId === undefined || layoutImages === undefined) continue;
      // A layout with its own image for this background shows that image in
      // the tile, so the tile changes with the layout.
      const layoutCandidates = layoutPreviewCandidates(routeTemplates, visible, layoutImages);
      const template = pickTilePreviewTemplate({
        candidates: layoutCandidates.length > 0 ? layoutCandidates : previewTemplates,
        adjustedOrder: bgAdjustmentOrder[String(entry.variation)],
        savedId,
      });
      if (!template) continue;
      const preset = getTileImage(template.id, entry.variation);
      if (preset === undefined) continue;
      const url = preset || entry.imageUrl;
      const thumbnailUrl = thumbnails.get(url);
      if (url)
        sources.set(String(entry.variation), {
          url,
          template,
          thumbnailUrl,
          needsThumbnail: !thumbnailUrl,
        });
    }
    return sources;
  }, [
    bgListEditing,
    listTiles,
    currentBgUrls,
    getTileSavedPreviewTemplate,
    getTileLayoutImages,
    routeTemplates,
    visible,
    previewTemplates,
    bgAdjustmentOrder,
    getTileImage,
  ]);
  const tileImages = [...tileSources.values()];
  const {data: tileImageSizes} = useBgImageSizes(
    tileImages.map((source) => source.url),
    Boolean(previewsEnabled && tileHeight),
    tileImages.filter((source) => source.needsThumbnail).map((source) => source.url)
  );

  /**
   * The crop preview of one tile, or null for a plain thumbnail: while the
   * crop or the image size loads, where the route shows no preview (ALL
   * products, livestream, ads), and for an image with no size.
   */
  const tilePreview = useCallback(
    (variation) => {
      const source = tileSources.get(String(variation));
      if (!tileHeight || !source) return null;
      const image = tileImageSizes?.[source.url];
      if (!image) return null;
      if (source.needsThumbnail && !image.thumbnailUrl) return null;
      const {template} = source;
      const transform = getTileTransform(template.id, variation);
      if (transform === undefined) return null;
      // The selected tile shows the live base, with the user's edits. Every
      // other tile shows the base its own background gets when picked.
      const tileBase =
        String(variation) === String(bgOption)
          ? (bases?.[template.id] ?? base)
          : getTileBase(template.id, variation);
      if (tileBase === undefined) return null;
      return {
        transform,
        image,
        thumbnailUrl: source.thumbnailUrl ?? image.thumbnailUrl,
        template,
        layout: tilePreviewLayout(template, tileHeight),
        baseColor: `#${tileBase}`,
      };
    },
    [tileHeight, tileImageSizes, tileSources, getTileTransform, getTileBase, bgOption, bases, base]
  );

  // The numbered tiles of the gallery, the hidden ones included. A link shows
  // its product's gallery; an empty one has no index, and still no legacy tile.
  // App numbers the same tiles for the URL (hooks/useBackgroundNumbers.ts).
  const numberedTiles = useMemo(
    () =>
      pageGalleryTiles({
        catalogIndex: controlled ? null : catalogIndex,
        displayOrder,
        linkGallery,
        allBgUrls,
        product: product.value,
        isAllProducts,
        isGoogleDisplayNetwork,
      }),
    [
      controlled,
      catalogIndex,
      displayOrder,
      linkGallery,
      allBgUrls,
      product.value,
      isAllProducts,
      isGoogleDisplayNetwork,
    ]
  );

  // A role without the list editor (admin, editSetBgOrder) can still give a
  // releases set its own order: the pencil opens the set's order only, with
  // no "All sets", no removal and no "Publish" (Sergey, 2026-11-03). The tiles
  // are the catalog gallery, already in the set's order (App's displayOrder).
  const canOrderSetOnly =
    !bgListEditing &&
    !controlled &&
    !sceneId &&
    features["bg.set-order"] &&
    setBgOrder.isAvailable &&
    Boolean(setLabel) &&
    Boolean(catalogIndex && displayOrder);
  const isSetOrderOnlyOpen = canOrderSetOnly && isBgEditMode;
  const setOnlyOrderEntries = useMemo(
    () =>
      canOrderSetOnly
        ? numberedTiles
            .filter((tile) => !isDraftUploadVariation(tile.variation))
            .map((tile, index) => ({
              entryKey: String(tile.variation),
              position: index + 1,
              variation: tile.variation,
              thumbnailUrl: tile.thumbnailUrl,
              imageUrl: tile.imageUrl,
            }))
        : [],
    [canOrderSetOnly, numberedTiles]
  );

  const getAllSrcs = useCallback(() => {
    processedRef.current = false;
    // Both the thumbnail (for the button bg) and the full-size source URL (for
    // the download icon's fetch + save flow) of each tile.
    setCurrentBgUrls(
      numberedTiles.map((tile) => ({
        index: tile.variation,
        url: tile.thumbnailUrl,
        fullUrl: tile.imageUrl,
      }))
    );
  }, [numberedTiles]);

  const transformBg = useCallback(
    (option) => {
      // Selection-only under bgDerivation: the crop for the newly selected
      // bgOption is derived by the effective-transform selector (its catalog /
      // resolved layers are keyed by bgOption), so writing it into the images
      // context here would be one of the three racing writers this refactor
      // removes. Do nothing and let the selector supply the value.
      if (bgDerivation) return;
      // The saved-scene selection applies the crop with its image and colors.
      if (sceneId && linkGallery !== null) return;

      let coords = null;

      if (transforms[product.value]) {
        coords = transforms[product.value][Number(option) - 1];
      }

      if (!coords && transforms["default"]) {
        coords = transforms["default"][Number(option) - 1];
      }

      const {x, y, scale, flip, rotate} = coords || DEFAULT_BACKGROUND_DATA;

      if (option === "generative") {
        editTransform(0, 0, 1, 0, {x: false, y: false});
      } else if (option !== "custom") {
        editTransform(x, y, scale, rotate, flip);
      }
    },
    [editTransform, transforms, product.value, bgDerivation, sceneId, linkGallery]
  );

  // The picked background is the same image on every banner again, detached
  // ones too: the banner images of the old background go. The default scene
  // of `value` then loads the images an admin saved per banner for it
  // (templateBg.ts presets). For the background on screen it loads nothing,
  // so its presets come back here. `clearsSharedUpload` drops the shared
  // upload (customBgs.main) too.
  const resetBannerImages = useCallback(
    (value, clearsSharedUpload) => {
      if (bannerImagesFollowBackground(dataChannel)) {
        const next = customBgsAfterBgSelect(
          customBgs,
          String(value) === String(bgOption) ? bgPresets : null
        );
        if (clearsSharedUpload) delete next.main;
        loadCustomBgs(next);
        loadCustomBgUploaded({});
      } else if (clearsSharedUpload) {
        editCustomBgs("main", undefined, false);
      }
    },
    [dataChannel, customBgs, bgOption, bgPresets, loadCustomBgs, loadCustomBgUploaded, editCustomBgs]
  );

  const onBgButtonClick = useCallback(
    (value) => {
      if (controlled) {controlled.onSelect(value); return;}
      track("neurobaker.background.selected", {background: value});
      // A pick for the selected banners only: the tile's image becomes theirs.
      if (scope.isSelection) {
        const picked = lookupBgImage(galleryIndex, Number(value), product.value)?.url;
        if (scope.applyBackgroundToSelection(picked)) return;
      }
      scope.yieldPresets("image");
      setSearchParams(
        (params) => {
          params.delete("privateBackground");
          return writeUrlBackground(params, value);
        },
        {replace: true}
      );
      transformBg(value);
      // A tile gives its variation as a number. `bgOption` holds a string,
      // as the URL gives it: a click on the selected tile changes no URL.
      editBgOption(String(value));
      editCustomBg("");
      // A link pins the image of the picked tile. The legacy tile with the
      // same number can hold an older image (libs/sceneBgSource.ts).
      if (linkGalleryIndex && sceneId) {
        const picked = lookupBgImage(linkGalleryIndex, Number(value), product.value);
        editPinnedSceneBg(
          picked?.url
            ? {sceneId, variation: Number(value), url: picked.url, product: product.value}
            : null
        );
      }
      // Ads renders customBgs.main over its numbered baked assets regardless
      // of bgOption, so picking a numbered background must also drop the
      // shared upload — otherwise the click is visually dead and the upload
      // unremovable. transformBg above already reset the stale cover-fit
      // transforms.
      resetBannerImages(
        value,
        shouldClearSharedUploadOnBgSelect({channelValue: dataChannel, customBgs})
      );

      // Reset blur when switching from custom to normal backgrounds (but not brightness for releases channel)
      if (dataChannel !== "releases") {
        editBgBrightness(100);
      }
      editBgBlurValue(0);
      editIsBgAnimation(false);
    },
    [
      controlled,
      scope,
      galleryIndex,
      linkGalleryIndex,
      sceneId,
      editPinnedSceneBg,
      transformBg,
      editBgOption,
      editCustomBg,
      customBgs,
      resetBannerImages,
      setSearchParams,
      writeUrlBackground,
      editBgBrightness,
      editBgBlurValue,
      editIsBgAnimation,
      channel,
      product.value,
    ]
  );

  // --- editable background list ---------------------------------------------
  // `bgOption` is the selected entry's IDENTITY (its variation), never its
  // position, so a reorder leaves the selection alone. Only an action that
  // takes the selected entry out of the list (or its tile off the page) moves
  // it — to the first entry. The banner images of the old entry go, as for a
  // click (2026-10-27, CL Screenshots: an admin hid the current background,
  // the Screenshot banner kept its image under the first tile, and a Save
  // wrote that image into the first background's default).
  const selectVariation = useCallback(
    (variation) => {
      const value = String(variation);
      resetBannerImages(value, false);
      editBgOption(value);
      setSearchParams(
        (params) => {
          params.delete("privateBackground");
          return writeUrlBackground(params, value);
        },
        {replace: true}
      );
    },
    [resetBannerImages, editBgOption, setSearchParams, writeUrlBackground]
  );

  const applyListChange = useCallback(
    async (change) => {
      const after = await change();
      // "custom" and "generative" are not list entries (Number() of either is
      // NaN): a list edit never touched them.
      const selected = Number(bgOption);
      if (after.length === 0 || !Number.isInteger(selected)) return;
      if (!hasVariation(after, selected)) {
        selectVariation(
          firstVisibleVariation(
            orderedBySet(
              after.map((entry) => entry.variation),
              (variation) => variation,
              setBgOrder.order
            ),
            hiddenBgs
          )
        );
      }
    },
    [bgOption, selectVariation, hiddenBgs, setBgOrder.order]
  );

  // A draft upload — a temporary tile of this page, or an admin's list draft —
  // has an identity only while it is listed: a reload drops a temporary tile,
  // a publish gives a draft a catalog variation. Then select the first
  // background rather than leave the canvas without one.
  useEffect(() => {
    if (!isDraftUploadVariation(bgOption) || !displayOrder?.length) return;
    if (!displayOrder.includes(Number(bgOption))) {
      selectVariation(firstVisibleVariation(displayOrder, hiddenBgs));
    }
  }, [bgOption, displayOrder, selectVariation, hiddenBgs]);

  // Livestream has one product-aware default tile. Existing products restore
  // editable per-template SVGs; Junie and .NET restore the selected PNG plus
  // its BiggerPicture crop.
  const isLivestream = channel.value === "livestream" && !controlled;
  // A built-in tile: an admin can hide it like a gallery tile.
  const hasGenerativeTile =
    !isLivestream && offersGenerativeTile(channel.value, product.value) && !hiddenBgs.has("generative");

  // A background that has no tile on this page does not exist for the page
  // (Sergey, 2026-10-26: "нет на этом канале и сете такого фона"): one that
  // an admin hid, one that the gallery does not hold (an admin's list draft
  // removed it), one with no image. A link, the last location or a reload can
  // still name it in `?background=`, and an admin can hide the background
  // that the page shows. Then select the first tile: the banners never show
  // a background that has no tile. A saved scene keeps its background, and
  // the link tile shows it. A product, stage or set switch resets the page
  // first (App.js); until then `bgOption` is the background of the page
  // before the switch. When the page has no numbered tile, select the
  // generative gradient.
  const isArriving = Boolean(location.state?.resetOnArrival);
  const numbered = useBackgroundNumbers();
  useEffect(() => {
    if (controlled || sceneId || isLivestream || isLoading || isArriving) return;
    if (!isRouteDefaultReady || !numbered) return;
    if (hasPageTile(String(bgOption), numbered, hasGenerativeTile)) return;
    if (numbered.length > 0) selectVariation(numbered[0]);
    else if (hasGenerativeTile) selectVariation("generative");
  }, [
    controlled,
    sceneId,
    isLivestream,
    isLoading,
    isArriving,
    isRouteDefaultReady,
    numbered,
    bgOption,
    hasGenerativeTile,
    selectVariation,
  ]);

  const handleBgReorder = useCallback(
    (fromKey, toKey) => applyListChange(() => bgListEditor.reorder(fromKey, toKey)),
    [applyListChange, bgListEditor]
  );

  // The set's order holds catalog backgrounds only: a draft upload gets its
  // identity when it is published.
  const setOrderEntries = useMemo(
    () => setOrderedList.filter((entry) => !isDraftUploadVariation(entry.variation)),
    [setOrderedList]
  );
  // The list editor's entries, or the gallery's for a set-order-only role.
  const activeSetOrderEntries = bgListEditing ? setOrderEntries : setOnlyOrderEntries;
  const handleSetOrderReorder = useCallback(
    (fromKey, toKey) => {
      const variationOf = (key) =>
        activeSetOrderEntries.find((entry) => entry.entryKey === key)?.variation;
      const from = variationOf(fromKey);
      const to = variationOf(toKey);
      if (from === undefined || to === undefined) return;
      setBgOrder.save(
        movedVariation(
          activeSetOrderEntries.map((entry) => entry.variation),
          from,
          to
        )
      );
    },
    [activeSetOrderEntries, setBgOrder]
  );
  // The editor stays on the set: the next drag edits the set's order again.
  const handleClearSetOrder = useCallback(() => setBgOrder.save([]), [setBgOrder]);

  const handleBgRemove = useCallback(
    (entryKey) => {
      setRemovedNoticeAt(Date.now());
      return applyListChange(() => bgListEditor.remove(entryKey));
    },
    [applyListChange, bgListEditor]
  );

  const handleBgUndo = useCallback(() => {
    setRemovedNoticeAt(0);
    return applyListChange(() => bgListEditor.undo());
  }, [applyListChange, bgListEditor]);

  const handleBgRedo = useCallback(
    () => applyListChange(() => bgListEditor.redo()),
    [applyListChange, bgListEditor]
  );

  const handleBgReset = useCallback(
    () => applyListChange(() => bgListEditor.reset()),
    [applyListChange, bgListEditor]
  );

  const handleBgPublish = useCallback(() => {
    // Publishing changes what other users see — say so before doing it.
    const confirmed = window.confirm(
      "Publish this background list for every user?\n\n" +
        "Backgrounds will appear in this order for everyone, and the ones you removed " +
        "will be hidden. Default scenes stay with their image wherever it moves."
    );
    if (confirmed) bgListEditor.publish();
  }, [bgListEditor]);

  // The "Background removed — Undo" bar is transient; it must not linger over
  // a list the user has since changed again.
  useEffect(() => {
    if (!removedNoticeAt) return undefined;
    const timer = setTimeout(() => setRemovedNoticeAt(0), 5000);
    return () => clearTimeout(timer);
  }, [removedNoticeAt]);

  const onBgUploadClick = (e) => {
    e.target.value = null;
  };

  // Cover-fit an uploaded image (natural dims imgW×imgH) across every template
  // in the current channel and commit the per-template transforms atomically —
  // background-size:cover + position:center, computed per template because each
  // has its own aspect ratio. Mirrors the per-template <CustomBgUpload>.
  // The same cover-fit the bg-list server computes for an upload entry
  // (computeCoverFitTransform in src/server/api/v3/template-sizes.ts), in the
  // catalog-item transform shape. Used to make the optimistic tile carry the
  // crop it will still have after the round trip.
  const coverFitRecords = useCallback(
    (imgW, imgH) => {
      const out = {};
      if (!imgW || !imgH) return out;
      for (const t of knownTemplatesFor(dataChannel, product.value)) {
        const {scale, x, y} = coverFitTransform(imgW, imgH, t.width, t.height);
        out[t.id] = {x, y, scale, rotate: 0, flip: {x: false, y: false}};
      }
      return out;
    },
    [dataChannel, product.value]
  );

  const coverFitChannel = useCallback(
    (imgW, imgH, targetBgOption = bgOption) => {
      const xRec = {};
      const yRec = {};
      const scaleRec = {};
      const rotateRec = {};
      const flipRec = {};
      for (const t of knownTemplatesFor(dataChannel, product.value)) {
        const {scale, x, y} = coverFitTransform(imgW, imgH, t.width, t.height);
        xRec[t.id] = x;
        yRec[t.id] = y;
        scaleRec[t.id] = scale;
        rotateRec[t.id] = 0;
        flipRec[t.id] = {x: false, y: false};
      }
      // Under bgDerivation the shared-upload cover-fit is explicit user intent
      // (per template, for the current bgOption); otherwise it writes the
      // context records directly. Either way each template keeps its own crop.
      if (bgDerivation) {
        for (const t of knownTemplatesFor(dataChannel, product.value)) {
          setUserBgAdjustment(targetBgOption, t.id, {
            x: xRec[t.id],
            y: yRec[t.id],
            scale: scaleRec[t.id],
            rotate: rotateRec[t.id],
            flip: flipRec[t.id],
          });
        }
        return;
      }
      editTransform(xRec, yRec, scaleRec, rotateRec, flipRec);
    },
    [dataChannel, product.value, editTransform, bgDerivation, bgOption, setUserBgAdjustment]
  );

  // The scope when an upload lands, not when it began: the mode table must not
  // lose an edit made while the file was uploading.
  const latestScope = useRef(scope);
  latestScope.current = scope;

  // An upload under a selection (scene scopes) goes to the selected banners
  // alone, as a tile click does there: each gets the file as an image of its
  // own, and the shared background stays. Upload-first like the other uploads;
  // an SVG stays inline, as in the per-banner upload (CustomBgUpload).
  const uploadToSelection = useCallback(
    async (file) => {
      const ids = scope.selection;
      if (isSvgFile(file)) {
        try {
          const dataUrl = await fileToDataUrl(file);
          latestScope.current.applyBackgroundToSelection(dataUrl, {uploaded: true, ids});
        } catch (err) {
          console.error("[BgSelector] SVG read failed", err);
          openErrorDialog({
            title: "Couldn't load background",
            summary: "The file couldn't be read. Try a different file.",
          });
        }
        return;
      }
      incPendingUpload();
      try {
        const result = await API.uploadFile(file);
        if (!result?.url) throw new Error("Upload API returned no URL");
        latestScope.current.applyBackgroundToSelection(result.url, {uploaded: true, ids});
      } catch (err) {
        console.error("[BgSelector] selection upload failed", err);
        openErrorDialog({
          title: "Couldn't upload background",
          summary: `The file wasn't uploaded${err?.message ? `: ${err.message}` : "."}`,
          diagnostics: err?.envelope,
        });
      } finally {
        decPendingUpload();
      }
    },
    [scope.selection, incPendingUpload, decPendingUpload]
  );

  const onBgFileUpload = useCallback(
    async (event) => {
      const file = event.target.files && event.target.files[0];
      if (controlled) {if (file) await controlled.onUpload(file); event.target.value = ""; return;}

      if (file) {
        // Validate file size before upload — nginx returns 413 with empty body
        // if the request exceeds its proxy-body-size limit, so we catch it here
        // with a helpful message showing the actual file size.
        if (file.size > UPLOAD_MAX_BYTES) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
          const maxMB = UPLOAD_MAX_BYTES / (1024 * 1024);
          openErrorDialog({
            title: "File too large",
            summary: `File is ${sizeMB} MB (max ${maxMB} MB). Please choose a smaller file.`,
            diagnostics: {fileName: file.name, fileSize: file.size, maxSize: UPLOAD_MAX_BYTES},
          });
          // Reset input so the same file can be re-selected
          event.target.value = "";
          return;
        }
        track("neurobaker.background.selected", {background: "custom"});
        // The selected banners take the file. The shared background, and its
        // animation flag, stay as they are.
        if (scope.isSelection) {
          await uploadToSelection(file);
          event.target.value = "";
          return;
        }
        // Check if the uploaded file is animated
        const isAnimated = await checkIfAnimated(file);

        editIsBgAnimation(isAnimated);

        // Livestream renders each template from customBgs[id] (inline data
        // URLs), not the shared customBg slot + gallery URL — and its
        // BgTransformers never run in bgOption "custom", so their built-in
        // cover-fit effect doesn't fire. So here we cover-fit every livestream
        // template explicitly (each has its own aspect) and write each slot.
        if (channel.value === "livestream") {
          try {
            const dataUrl = await fileToDataUrl(file);
            const {width, height} = await getSizeFromBase64(dataUrl);
            coverFitChannel(width, height);
            // .main is the canonical shared copy — templates render from their
            // per-id slot (over the seeded baked), and a template reverts to
            // .main when it's unlocked (see TemplateControls).
            editCustomBgs("main", dataUrl, false);
            for (const t of knownTemplates.livestream || []) {
              editCustomBgs(t.id, dataUrl, true);
              editCustomBgUploaded(t.id, true);
            }
          } catch (err) {
            console.error("[BgSelector] livestream upload failed", err);
            openErrorDialog({
              title: "Couldn't load background",
              summary: "The file couldn't be read. Try a different file.",
            });
          } finally {
            event.target.value = "";
          }
          return;
        }

        // An upload is a new tile (libs/sessionBgUploads.ts): the uploader's
        // only, until the page reloads, and selected as a click on it would.
        // "Save as default" makes it the product's first background for
        // everyone. With the list editor open (the pencil) it is a draft of
        // the admin's list instead, below. Ads (bundled galleries), SVG (the
        // gradient editor reads it inline) and video keep the custom slot.
        const tileable =
          bgDerivation &&
          !isAllProducts &&
          channel.value !== "ads" &&
          !isSvgFile(file) &&
          !file.type.startsWith("video/");
        if (tileable && !(bgListEditing && isBgEditorOpen)) {
          incPendingUpload();
          try {
            const dataUrl = await fileToDataUrl(file);
            const {width, height} = await getSizeFromBase64(dataUrl);
            const result = await API.uploadFile(file);
            if (!result?.url || !result?.id) throw new Error("Upload API returned no URL");
            const entry = {
              entryKey: `upload:${result.id}`,
              kind: "upload",
              uploadId: result.id,
              sourceVariation: null,
              position: 0,
              variation: draftUploadVariation(result.id),
              imageUrl: result.url,
              thumbnailUrl: result.url,
              miniUrl: null,
              outdatedUrl: null,
              width,
              height,
              base: null,
              markers: [],
              transforms: coverFitRecords(width, height),
              hasStoredTransforms: true,
            };
            addSessionBgUpload(sessionBgScope(dataChannel, dataType, product.value), entry);
            onBgButtonClick(entry.variation);
          } catch (err) {
            console.error("[BgSelector] background upload failed", err);
            openErrorDialog({
              title: "Couldn't upload background",
              summary: `The image wasn't added to the backgrounds${err?.message ? `: ${err.message}` : "."}`,
              diagnostics: err?.envelope,
            });
          } finally {
            decPendingUpload();
            event.target.value = "";
          }
          return;
        }

        // Editable background list: an upload becomes a new LAST tile in the
        // gallery — deletable, reorderable, and persisted per user — instead of
        // filling the transient `custom` slot. SVG deliberately stays on the
        // custom path below: the server's upload allowlist keeps it out of the
        // gallery, and only an inline data URL lets the gradient editor read it.
        // Ads uses the shared customBgs.main slot below because every Ads
        // template renders its own background source rather than customBg.
        if (channel.value !== "ads" && bgListEditing && !isSvgFile(file)) {
          incPendingUpload();
          try {
            const dataUrl = await fileToDataUrl(file);
            const {width, height} = await getSizeFromBase64(dataUrl);
            const result = await API.uploadFile(file);
            if (!result?.url) throw new Error("Upload API returned no URL");
            const entry = {
              entryKey: `upload:${result.id}`,
              kind: "upload",
              sourceVariation: null,
              // Rewritten by the editor to the real tail position.
              position: bgListEditor.list.length + 1,
              variation: draftUploadVariation(result.id),
              imageUrl: result.url,
              // Thumbnails for uploads are a non-goal — the full image is the tile.
              thumbnailUrl: result.url,
              miniUrl: null,
              outdatedUrl: null,
              width,
              height,
              base: null,
              markers: [],
              // The same cover-fit the server computes for this entry, so the
              // optimistic tile and the persisted one describe one crop.
              transforms: coverFitRecords(width, height),
              hasStoredTransforms: true,
            };
            bgListEditor.addUpload(entry);
            selectVariation(entry.variation);
          } catch (err) {
            console.error("[BgSelector] gallery upload failed", err);
            openErrorDialog({
              title: "Couldn't upload background",
              summary: `The image wasn't added to the gallery${err?.message ? `: ${err.message}` : "."}`,
              diagnostics: err?.envelope,
            });
          } finally {
            decPendingUpload();
            event.target.value = "";
          }
          return;
        }

        editBgOption("custom");
        setSearchParams(
          (params) => {
            params.delete("privateBackground");
            return writeBackgroundParam(params, "custom", null);
          },
          {replace: true}
        );
        const storesSharedAdsBg = channel.value === "ads";

        // Cover-fit for these channels is applied per-template by
        // BgTransformer's own effect (it fires on bgOption "custom" once the
        // image dimensions are known). Ads renders from customBgs.main instead,
        // so its upload path computes the per-template cover-fit explicitly.

        // SVG is kept as a local base64 data URL instead of being uploaded
        // to the gallery — the same approach as the Ads per-template custom
        // bg. Two reasons: (1) the server's upload allowlist rejects
        // image/svg+xml (it can carry script), and (2) only an inline SVG
        // data URL lets BiggerPicture inline it (sanitized via DOMPurify)
        // so the gradient-stop editor can detect and edit its <stop>s. The
        // data URL lives in customBg, which is part of IMAGES_SCENE_FIELDS,
        // so it round-trips on scene save without touching the server.
        if (isSvgFile(file)) {
          try {
            const dataUrl = await fileToDataUrl(file);
            if (storesSharedAdsBg) {
              const {width, height} = await getSizeFromBase64(dataUrl);
              coverFitChannel(width, height, "custom");
              editCustomBgs("main", dataUrl, false);
            } else {
              editCustomBg(dataUrl);
            }
          } catch (err) {
            console.error("[BgSelector] SVG read failed", err);
            openErrorDialog({
              title: "Couldn't load SVG",
              summary: "The SVG file couldn't be read. Try a different file.",
            });
          } finally {
            event.target.value = "";
          }
          return;
        }

        // Upload + scene composition are orthogonal actions (#169 design):
        // the file goes to the per-user gallery first, the scene only ever
        // holds the resulting CDN URL — never the raw blob. For instant
        // preview, set a local `blob:` URL on the channel's rendered slot, then
        // swap to the CDN URL when the upload completes. `pendingUploadCount`
        // blocks scene save while the swap is pending — otherwise a save would
        // persist a `blob:` URL that doesn't survive a tab close.
        const localUrl = URL.createObjectURL(file);
        if (storesSharedAdsBg) {
          editCustomBgs("main", localUrl, false);
        } else {
          editCustomBg(localUrl);
        }
        if (visible[0]) {
          editIsBgToolsExpanded(visible[0], true);
        }
        editIsImageToolsExpanded(false);
        editCurrentImageToolsId(null);
        incPendingUpload();
        try {
          if (storesSharedAdsBg) {
            const dataUrl = await fileToDataUrl(file);
            const {width, height} = await getSizeFromBase64(dataUrl);
            coverFitChannel(width, height, "custom");
          }
          const result = await API.uploadFile(file);
          if (!result?.url) {
            // Server returned 200 with a missing/empty url field — treat as
            // a contract violation. CLAUDE.md: fail fast at internal
            // boundaries; silently calling editCustomBg(undefined) would
            // leave the user with no background and no explanation.
            throw new Error("Upload API returned no URL");
          }
          if (storesSharedAdsBg) {
            editCustomBgs("main", result.url, false);
          } else {
            editCustomBg(result.url);
          }
        } catch (err) {
          console.error("[BgSelector] upload failed", err);
          // Revert the rendered slot to its last durable value. Reading from a
          // ref avoids restoring another upload's revoked local preview URL.
          if (storesSharedAdsBg) {
            editCustomBgs("main", lastCommittedAdsBgRef.current, false);
          } else {
            editCustomBg(lastCommittedBgRef.current);
          }
          openErrorDialog({
            title: "Couldn't upload background",
            summary:
              err?.envelope?.error?.isLikelyCors || err?.name === "NetworkError"
                ? "The upload didn't reach the server. Check your connection and try again."
                : err?.status === 413
                  ? `The file is too large (max ${UPLOAD_MAX_BYTES / (1024 * 1024)} MB). Try a smaller file.`
                  : err?.status === 400 || err?.status === 415
                    ? "Unsupported file type. Use PNG, JPEG, WebP, GIF, MP4, WebM, or MOV."
                    : `Upload failed${err?.message ? `: ${err.message}` : ""}.`,
            diagnostics: err?.envelope,
          });
        } finally {
          URL.revokeObjectURL(localUrl);
          decPendingUpload();
        }
      }
    },
    [
      controlled,
      channel.value,
      coverFitChannel,
      editBgOption,
      editCustomBg,
      editCustomBgs,
      editCustomBgUploaded,
      setSearchParams,
      visible,
      editIsBgToolsExpanded,
      editIsImageToolsExpanded,
      editCurrentImageToolsId,
      editIsBgAnimation,
      incPendingUpload,
      decPendingUpload,
      bgListEditing,
      bgListEditor,
      coverFitRecords,
      selectVariation,
      onBgButtonClick,
      bgDerivation,
      isAllProducts,
      dataChannel,
      dataType,
      product,
      isBgEditorOpen,
      scope.isSelection,
      uploadToSelection,
    ]
  );

  // The upload tile spins from the file pick until the upload ends, also when
  // the upload fails. It is a count: a second pick can start before the first
  // upload ends.
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const onBgFilePick = useCallback(
    async (event) => {
      setUploadsInFlight((count) => count + 1);
      try {
        await onBgFileUpload(event);
      } finally {
        setUploadsInFlight((count) => count - 1);
      }
    },
    [onBgFileUpload]
  );

  useEffect(() => {
    getAllSrcs();
  }, [getAllSrcs]);

  useEffect(() => {
    // Legacy tile path only: a catalog tile keeps its own variation.
    if (
      !isCatalogGallery &&
      currentBgUrls.length === 1 &&
      currentBgUrls[0] &&
      currentBgUrls[0].index !== 1 &&
      !processedRef.current
    ) {
      processedRef.current = true;
      setCurrentBgUrls([{index: 1, url: currentBgUrls[0].url}]);
    } else if (currentBgUrls.length !== 1) {
      processedRef.current = false;
    }
  }, [currentBgUrls, isCatalogGallery]);

  useEffect(() => {
    const newLoadingStates = {};
    const variations = isCatalogGallery ? galleryOrder : (allBgUrls ?? []).map((_, i) => i + 1);
    for (const variation of variations) {
      newLoadingStates[variation] = !getUrlAtIndex(variation);
    }
    setLoadingStates(newLoadingStates);
  }, [currentBgUrls, product.value, allBgUrls, galleryOrder, isCatalogGallery, getUrlAtIndex]);

  // --- the link tile (linkTile.ts) ---------------------------------------------
  // The background a saved link opened with, when no gallery tile shows it: a
  // dashed tile. A click brings it back; an admin can make an upload, or a
  // background hidden on this page, a real background, the first one of the
  // gallery.
  const linkImageUrl = useMemo(() => {
    if (!linkBackground) return null;
    if (linkBackground.bgOption === "custom") return linkBackground.customBg;
    return deriveBgUrl({
      bgOption: linkBackground.bgOption,
      productValue: product.value,
      allBgUrls,
      customBg: null,
      catalogIndex: null,
      catalogFallbackIndex: linkGalleryIndex,
      pinnedUrl: linkBackground.pin?.url ?? null,
    }).url;
  }, [linkBackground, product.value, allBgUrls, linkGalleryIndex]);
  // A background hidden in the Defaults panel has no tile, so a link that
  // names one gets the link tile for it.
  const linkTile = useMemo(
    () =>
      linkTileOf({
        link: linkBackground,
        gallery: linkGallery ?? [],
        hidden: hiddenBgs,
        imageUrl: linkImageUrl,
      }),
    [linkBackground, linkGallery, linkImageUrl, hiddenBgs]
  );
  // The tile shows a small image, never the original one: a legacy tile has
  // its icon, an inline SVG draws as itself, and the server makes a WebP icon
  // of any other image (POST /api/v3/backgrounds/image-sizes).
  const linkImage = linkTile?.imageUrl ?? "";
  const isLegacyTileImage = linkImage.includes("/kraken/tiles/");
  const linkIconSource =
    /^https?:\/\//.test(linkImage) && !isLegacyTileImage ? [linkImage] : EMPTY_BG_URLS;
  const {data: linkIconSizes} = useBgImageSizes(
    linkIconSource,
    linkIconSource.length > 0,
    linkIconSource
  );
  const linkIcon = isLegacyTileImage
    ? getThumbnailUrl(linkImage)
    : linkImage.startsWith("data:image/svg+xml")
      ? linkImage
      : linkIconSizes?.[linkImage]?.thumbnailUrl;
  const isLinkTileCurrent =
    Boolean(linkTile) &&
    showsLinkBackground(linkBackground, {bgOption, customBg, pin: pinnedSceneBg});
  const linkPinUrl = activePinnedBgUrl(pinnedSceneBg, {
    sceneId,
    bgOption,
    productValue: product.value,
  });
  const isGalleryTileCurrent = (variation, imageUrl) =>
    linkGallery
      ? !isLinkTileCurrent &&
        isLinkGalleryTileCurrent({variation, imageUrl, bgOption, customBg, pinUrl: linkPinUrl})
      : +bgOption === variation;

  const restoreLinkBackground = useCallback(() => {
    track("neurobaker.background.selected", {background: "link"});
    // Without a background in the URL the link opens its own background again.
    setSearchParams(
      (params) => {
        params.delete("privateBackground");
        return writeBackgroundParam(params, null, null);
      },
      {replace: true}
    );
    editBgOption(linkBackground.bgOption);
    editPinnedSceneBg(linkBackground.pin);
    editCustomBg(linkBackground.customBg);
    applyBackgroundAppearance(linkBackground.appearance);
  }, [
    linkBackground,
    setSearchParams,
    editBgOption,
    editPinnedSceneBg,
    editCustomBg,
    applyBackgroundAppearance,
  ]);

  // --- "+": an upload becomes a background (hooks/usePromoteUpload.ts) ---------
  // On the link tile and on a temporary upload tile, for the roles that save
  // defaults. The upload, or the link's hidden background, becomes the first
  // background of the gallery.
  // "bg.promote-upload" requires editDefaultScenes (data/features.ts).
  const canAddUploads = features["bg.promote-upload"];
  const {
    mutateAsync: promoteUpload,
    isPending: isPromotingUpload,
    variables: promotingUpload,
  } = usePromoteUpload();
  // On a releases set page only that set gets the image, as a drag edits only
  // the set's order: the other sets of the product do not show it. To give it
  // to every set, choose "All sets" with the pencil on (Sergey, 2026-10-30).
  const additionSet =
    setBgOrder.isAvailable && setLabel && isSetOrderScope ? setBgOrder.set : undefined;
  const addUploadToBackgrounds = useCallback(
    async (input) => {
      if (isPromotingUpload) return;
      // This changes what every user sees — say so before doing it.
      const confirmed = window.confirm(
        additionSet
          ? `Add this image to the ${product.label} backgrounds of ${setLabel}?\n\n` +
              `It becomes the first background of ${setLabel} for everyone. ` +
              "The other sets do not get it: to add it to every set, choose " +
              '"All sets" with the pencil.'
          : `Add this image to the ${product.label} backgrounds?\n\n` +
              "It becomes the first background of the gallery for everyone. " +
              "Change its place with the pencil on the product page."
      );
      if (!confirmed) return;
      try {
        // The gallery lists the image now, so its real tile takes the place of
        // the dashed one.
        await promoteUpload({...input, set: additionSet});
      } catch (err) {
        console.error("[BgSelector] adding an uploaded background failed", err);
        openErrorDialog({
          title: "Couldn't add the background",
          summary: `The image wasn't added to the backgrounds${err?.message ? `: ${err.message}` : "."}`,
          diagnostics: err?.envelope,
        });
      }
    },
    [isPromotingUpload, additionSet, setLabel, product.label, promoteUpload]
  );
  const linkTileAddition = linkTile?.variation
    ? {variation: linkTile.variation}
    : linkTile?.uploadId
      ? {uploadId: linkTile.uploadId}
      : null;
  const isAddingLinkBg =
    isPromotingUpload &&
    Boolean(linkTileAddition) &&
    promotingUpload?.uploadId === linkTileAddition.uploadId &&
    promotingUpload?.variation === linkTileAddition.variation;
  // A temporary upload tile (libs/sessionBgUploads.ts) is dashed, as the link
  // tile: it is no background of the gallery yet (Sergey, 2026-10-20).
  const sessionTileProps = (variation) => {
    const upload = sessionUploads.find((entry) => entry.variation === Number(variation));
    if (!upload) return null;
    return {
      className: "bg-button--upload",
      title: "Your upload. Only you see it, until the page reloads.",
      isLoading: isPromotingUpload && promotingUpload?.uploadId === upload.uploadId,
      onAdd:
        canAddUploads && !isPromotingUpload
          ? () =>
              addUploadToBackgrounds({
                uploadId: upload.uploadId,
                sessionUpload: upload,
                shownAs: String(upload.variation),
              })
          : undefined,
    };
  };

  const isCustomBg = bgOption === "custom";

  const onCssBgSelect = useCallback(() => {
    onBgButtonClick("generative");
  }, [onBgButtonClick]);

  const livestreamArtwork = useMemo(
    () => getLivestreamSelectorBackground(product.value, customBgs),
    [product.value, customBgs]
  );
  const restoreLivestreamDefault = useCallback(() => {
    const backgrounds = getLivestreamSeedBackgrounds(product.value);
    const x = {};
    const y = {};
    const scale = {};
    const rotate = {};
    const flip = {};

    loadCustomBgs(backgrounds);
    for (const tid of LIVESTREAM_TEMPLATE_IDS) {
      editCustomBgUploaded(tid, false);
      const transform = getLivestreamDefaultTransform(product.value, tid) || {
        x: 0,
        y: 0,
        scale: 1,
      };
      if (bgDerivation) {
        setUserBgAdjustment(bgOption, tid, {
          ...transform,
          rotate: 0,
          flip: {x: false, y: false},
        });
      } else {
        x[tid] = transform.x;
        y[tid] = transform.y;
        scale[tid] = transform.scale;
        rotate[tid] = 0;
        flip[tid] = {x: false, y: false};
      }
    }
    if (!bgDerivation) editTransform(x, y, scale, rotate, flip);
  }, [
    product.value,
    loadCustomBgs,
    editCustomBgUploaded,
    editTransform,
    bgDerivation,
    bgOption,
    setUserBgAdjustment,
  ]);

  return (
    <div className={cn("bg-selector-wrapper", {"all-products-mode": isAllProducts})}>
      {bgListEditing && features["bg.list-editor"] && (
        <BgEditToggle
          isOn={isBgEditMode}
          onToggle={() => {
            setIsBgEditMode((on) => !on);
            setOrderScopeChoice(null);
          }}
        />
      )}
      {isBgEditorOpen && (
        <BgListControls
          canUndo={bgListEditor.canUndo}
          canRedo={bgListEditor.canRedo}
          isSaving={bgListEditor.isSaving}
          canPublish={can(role, "editBgList")}
          onUndo={handleBgUndo}
          onRedo={handleBgRedo}
          onReset={handleBgReset}
          onPublish={handleBgPublish}
          orderScope={
            setBgOrder.isAvailable && setLabel
              ? {
                  setLabel,
                  isSetScope: isSetOrderScope,
                  hasSetOrder: setBgOrder.order !== null,
                  onScopeChange: chooseOrderScope,
                  onClearSetOrder: handleClearSetOrder,
                }
              : undefined
          }
        />
      )}
      {canOrderSetOnly && (
        <BgEditToggle
          isOn={isBgEditMode}
          onToggle={() => setIsBgEditMode((on) => !on)}
          setLabel={setLabel}
        />
      )}
      {isSetOrderOnlyOpen && (
        <SetOrderControls
          scope={{
            setLabel,
            isSetScope: true,
            hasSetOrder: setBgOrder.order !== null,
            canChooseAllSets: false,
            onScopeChange: () => {},
            onClearSetOrder: handleClearSetOrder,
          }}
        />
      )}
      {isBgEditorOpen && removedNoticeAt > 0 && <BgRemovedNotice onUndo={handleBgUndo} />}
      <div className="bg-selector">
        {/* Shared custom-background upload, on every channel. It cover-fits the
         * same image into each template (Livestream/Ads write customBgs; other
         * channels use the customBg scalar). Per-template uploads live on the
         * template-controls button, shown only when a template is detached. */}
        {(features["bg.upload"] || bgOption === "custom") && (
          <UploadButton
            key="custom"
            value="custom"
            handleChange={onBgFilePick}
            handleClick={onBgUploadClick}
            isCurrent={bgOption === "custom"}
            isUploading={uploadsInFlight > 0}
          />
        )}
        {isLivestream && (
          <Button
            key="livestream-default"
            value="livestream-default"
            src={livestreamArtwork.src}
            fullSrc={livestreamArtwork.fullSrc}
            handleClick={restoreLivestreamDefault}
            isCurrent
          />
        )}
        {/* Pencil off: the editor's list in its order, as plain tiles — no
         * drag, no remove, no download. Pencil on: the sortable editor. A
         * background hidden in the Defaults panel gets no tile in any mode. */}
        {!isLoading && !isLivestream && bgListEditing && !isBgEditorOpen
          ? listTiles
              .filter(
                (entry) => (entry.thumbnailUrl || entry.imageUrl) && !hiddenBgs.has(entry.variation)
              )
              .map((entry) => (
                <Button
                  key={entry.entryKey}
                  value={entry.variation}
                  src={entry.thumbnailUrl !== entry.imageUrl ? entry.thumbnailUrl : undefined}
                  handleClick={onBgButtonClick}
                  isCurrent={+bgOption === entry.variation}
                  preview={tilePreview(entry.variation)}
                  {...sessionTileProps(entry.variation)}
                />
              ))
          : null}
        {!isLoading && !isLivestream && isEditingSetOrder ? (
          <BgListTiles
            // The set's own order: a drag saves it for everyone at once. A
            // removal is for every set, so it stays in the product's order.
            entries={setOrderEntries.filter(
              (entry) => (entry.thumbnailUrl || entry.imageUrl) && !hiddenBgs.has(entry.variation)
            )}
            onReorder={handleSetOrderReorder}
            renderTile={(entry) => (
              <Button
                value={entry.variation}
                src={entry.thumbnailUrl !== entry.imageUrl ? entry.thumbnailUrl : undefined}
                fullSrc={entry.imageUrl}
                handleClick={onBgButtonClick}
                isCurrent={+bgOption === entry.variation}
                preview={tilePreview(entry.variation)}
              />
            )}
          />
        ) : null}
        {!isLoading && !isLivestream && isBgEditorOpen && !isEditingSetOrder ? (
          <BgListTiles
            // Same rule the legacy branch below applies (`if (!item.url) return
            // null`): an entry with no display image for THIS product gets no
            // tile. It STAYS in the editor list — its position belongs to it —
            // so a filtered view here must not feed the editor itself.
            entries={bgListEditor.list.filter(
              (entry) => (entry.thumbnailUrl || entry.imageUrl) && !hiddenBgs.has(entry.variation)
            )}
            onReorder={handleBgReorder}
            renderTile={(entry) => (
              <Button
                value={entry.variation}
                src={entry.thumbnailUrl !== entry.imageUrl ? entry.thumbnailUrl : undefined}
                fullSrc={entry.imageUrl}
                handleClick={onBgButtonClick}
                isCurrent={+bgOption === entry.variation}
                onRemove={() => handleBgRemove(entry.entryKey)}
                preview={tilePreview(entry.variation)}
              />
            )}
          />
        ) : null}
        {!isLoading && !isLivestream && isBgEditorOpen
          ? sessionTilesAfterList.map((entry) => (
              <Button
                key={entry.entryKey}
                value={entry.variation}
                src={entry.thumbnailUrl !== entry.imageUrl ? entry.thumbnailUrl : undefined}
                handleClick={onBgButtonClick}
                isCurrent={+bgOption === entry.variation}
                preview={tilePreview(entry.variation)}
                {...sessionTileProps(entry.variation)}
              />
            ))
          : null}
        {!isLoading && !isLivestream && isSetOrderOnlyOpen ? (
          <BgListTiles
            // The set's own order: a drag saves it for everyone at once.
            entries={setOnlyOrderEntries.filter(
              (entry) => entry.thumbnailUrl && !hiddenBgs.has(entry.variation)
            )}
            onReorder={handleSetOrderReorder}
            renderTile={(entry) => (
              <Button
                value={entry.variation}
                src={entry.thumbnailUrl}
                fullSrc={entry.imageUrl}
                handleClick={onBgButtonClick}
                isCurrent={+bgOption === entry.variation}
                preview={tilePreview(entry.variation)}
              />
            )}
          />
        ) : null}
        {!isLoading && !isLivestream && !bgListEditing && !isSetOrderOnlyOpen
          ? currentBgUrls.map((item) => {
              if (!item || !item.url || hiddenBgs.has(item.index)) return null;
              const uploadTile = sessionTileProps(item.index);

              return (
                <Button
                  key={item.index}
                  value={item.index}
                  src={item.url}
                  handleClick={onBgButtonClick}
                  isCurrent={isGalleryTileCurrent(item.index, item.fullUrl)}
                  preview={tilePreview(item.index)}
                  {...uploadTile}
                  isLoading={loadingStates[item.index] || uploadTile?.isLoading}
                />
              );
            })
          : null}
        {hasGenerativeTile && (features["bg.generative"] || bgOption === "generative") && (
          <GenerativeButton
            key="generative"
            handleClick={onCssBgSelect}
            isCurrent={bgOption === "generative"}
          />
        )}
        {!isLoading && linkTile && (
          <Button
            key="link"
            value="link"
            className="bg-button--link"
            title="The background of this link. It is not in the gallery."
            src={linkIcon}
            handleClick={restoreLinkBackground}
            isCurrent={isLinkTileCurrent}
            isLoading={isAddingLinkBg}
            onAdd={
              linkTileAddition && canAddUploads && !isPromotingUpload
                ? () => addUploadToBackgrounds(linkTileAddition)
                : undefined
            }
          />
        )}
      </div>

      {!controlled && <PointCloudBackgrounds />}

      {/* Base color */}
      {features["bg.base-color"] && (
      <div className="bg-base-color  bg-section">
        <label htmlFor="base-color">Base color</label>
        <ColorPicker
          value={parseColorInput(scope.scopedBase) ?? "#000000"}
          onChange={(hex) => scope.applyBase(hex.slice(1))}
          ariaLabel="Base color"
          className="base-color-picker"
        />
        <div className="base-color-input-wrapper">
          <span className="base-color-hash">#</span>
          {/* The fill the artwork sits on, and part of a banner's look: under a
              selection it goes to those banners alone, so the field has to show
              what it would edit rather than the shared colour. */}
          <BaseColorInput
            key={JSON.stringify([dataChannel, product.code, dataType, sceneId, scope.selection])}
            value={scope.scopedBase}
            onChange={scope.applyBase}
          />
        </div>
      </div>
      )}

      {/* Animation controls for generative background */}
      {bgOption === "generative" && features["bg.generative-animation"] && (
        <div className="bg-animation bg-section">
          <div className="animation-header">
            <span className="section-label">Animation</span>
            <div className="animation-buttons">
              <button
                className="animation-btn"
                onClick={toggleAnimationPaused}
                title={isAnimationPaused ? "Play" : "Pause"}
              >
                {isAnimationPaused ? "\u25B6" : "\u23F8"}
              </button>
              <button
                className="animation-btn regenerate-btn"
                onClick={controlled ? controlled.onRegenerate : regenerateGradients}
                title="Regenerate"
              >
                ⟳
              </button>
            </div>
          </div>
          <div className="slider-control">
            <label htmlFor="gen-speed">Duration</label>
            <input
              id="gen-speed"
              type="range"
              min="2000"
              max="40000"
              step="1000"
              value={generativeSpeed}
              onChange={(e) => editGenerativeSpeed(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">{(generativeSpeed / 1000).toFixed(1)}s</span>
          </div>
          <div className="slider-control">
            <label htmlFor="gen-count">Elements</label>
            <input
              id="gen-count"
              type="range"
              min="3"
              max="20"
              step="1"
              value={generativeCount || generativeGradients?.gradients?.length || 10}
              onChange={(e) => (controlled ? controlled.onCount : editGenerativeCount)(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">
              {generativeCount || generativeGradients?.gradients?.length || 10}
            </span>
          </div>
          <div className="slider-control">
            <label htmlFor="gen-stops">Waypoints</label>
            <input
              id="gen-stops"
              type="range"
              min="3"
              max="8"
              step="1"
              value={generativeStops}
              onChange={(e) => (controlled ? controlled.onStops : editGenerativeStops)(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">{generativeStops}</span>
          </div>
        </div>
      )}

      {/* Sliders for custom backgrounds */}
      {isCustomBg && dataChannel !== "releases" && features["bg.look-sliders"] && (
        <div className="bg-sliders">
          <div className="slider-control">
            <label htmlFor="bg-brightness">Brightness</label>
            <input
              id="bg-brightness"
              type="range"
              min="0"
              max="200"
              value={bgBrightness}
              onChange={(e) => editBgBrightness(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">{bgBrightness}%</span>
          </div>
          <div className="slider-control">
            <label htmlFor="bg-blur">Blur</label>
            <input
              id="bg-blur"
              type="range"
              min="0"
              max="20"
              value={bgBlurValue}
              onChange={(e) => editBgBlurValue(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">{bgBlurValue}px</span>
          </div>
          <OpacitySlider id="bg-opacity" value={bgOpacity} onChange={editBgOpacity} />
        </div>
      )}

      {/* Brightness control for releases channel (moved from Toolbar) */}
      {dataChannel === "releases" && features["bg.look-sliders"] && (
        <div className="bg-sliders">
          <div className="slider-control">
            <label htmlFor="bg-brightness-releases">Brightness</label>
            <input
              id="bg-brightness-releases"
              type="range"
              min="0"
              max="200"
              value={scope.scopedBrightness}
              onChange={(e) => scope.applyBrightness(Number(e.target.value))}
              className="slider"
            />
            <span className="slider-value">{scope.scopedBrightness}%</span>
          </div>
          {/* Both sliders show what the scope would edit, not the shared value:
              under a selection the edit goes to the banners' own records, and a
              control bound to the shared number would snap back mid-drag. */}
          <OpacitySlider
            id="bg-opacity-releases"
            value={scope.scopedOpacity}
            onChange={scope.applyOpacity}
          />
        </div>
      )}
    </div>
  );
};

export default BgSelector;
